import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PasswordInput } from '@/components/password-input';
import { authClient, ROLE_LABEL, useMe } from '@/lib/auth';
import { formatDateTime } from '@/lib/format';
import { toastError, toastSuccess } from '@/lib/notify';

export const Route = createFileRoute('/_app/account')({
  component: AccountPage,
});

function AccountPage() {
  const { user } = useMe();
  return (
    <>
      <PageHeader title="My account" />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>
              Your name and role are managed by an admin or developer.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[6rem_1fr] gap-y-3 text-sm">
              <dt className="text-muted-foreground">Name</dt>
              <dd>{user.name}</dd>
              <dt className="text-muted-foreground">Email</dt>
              <dd className="break-all">{user.email}</dd>
              <dt className="text-muted-foreground">Role</dt>
              <dd>
                <Badge variant="secondary">{ROLE_LABEL[user.role]}</Badge>
              </dd>
            </dl>
          </CardContent>
        </Card>
        <ChangePassword />
        <Sessions />
      </div>
    </>
  );
}

const ChangePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: z.string().min(8, 'Use at least 8 characters').max(128),
    confirm: z.string(),
    revokeOtherSessions: z.boolean(),
  })
  .refine((v) => v.newPassword === v.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });
type ChangePasswordValues = z.infer<typeof ChangePasswordSchema>;

function ChangePassword() {
  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirm: '', revokeOtherSessions: true },
  });

  async function onSubmit(v: ChangePasswordValues) {
    const { error } = await authClient.changePassword({
      currentPassword: v.currentPassword,
      newPassword: v.newPassword,
      revokeOtherSessions: v.revokeOtherSessions,
    });
    if (error) {
      form.setError('currentPassword', {
        message:
          error.code === 'INVALID_PASSWORD'
            ? 'Current password is incorrect'
            : (error.message ?? 'Failed'),
      });
      return;
    }
    form.reset();
    toastSuccess('Password changed');
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change password</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
            {(
              [
                ['currentPassword', 'Current password', 'current-password'],
                ['newPassword', 'New password', 'new-password'],
                ['confirm', 'Confirm new password', 'new-password'],
              ] as const
            ).map(([name, label, autoComplete]) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                      <PasswordInput autoComplete={autoComplete} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
            <FormField
              control={form.control}
              name="revokeOtherSessions"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(v) => field.onChange(v === true)}
                    />
                  </FormControl>
                  <FormLabel className="font-normal">Sign out my other sessions</FormLabel>
                </FormItem>
              )}
            />
            <Button type="submit" disabled={form.formState.isSubmitting}>
              Change password
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

function Sessions() {
  const queryClient = useQueryClient();
  const current = authClient.useSession();
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => {
      const { data, error } = await authClient.listSessions();
      if (error) throw new Error(error.message ?? 'Could not load sessions');
      return data;
    },
  });
  const revokeOthers = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.revokeOtherSessions();
      if (error) throw new Error(error.message ?? 'Could not sign out other sessions');
    },
    onSuccess: () => {
      toastSuccess('Other sessions signed out');
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (err) => toastError(err),
  });

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="flex-row items-start justify-between">
        <div>
          <CardTitle>Active sessions</CardTitle>
          <CardDescription>Browsers where you are signed in.</CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={revokeOthers.isPending || (sessions.data?.length ?? 0) < 2}
          onClick={() => revokeOthers.mutate()}
        >
          Sign out other sessions
        </Button>
      </CardHeader>
      <CardContent>
        <ul className="divide-y text-sm">
          {sessions.data?.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span className="max-w-xl truncate text-muted-foreground">
                {s.userAgent ?? 'Unknown browser'}
              </span>
              <span className="flex items-center gap-2">
                {s.ipAddress && <span className="font-mono text-xs">{s.ipAddress}</span>}
                <span className="text-xs text-muted-foreground">
                  since {formatDateTime(s.createdAt)}
                </span>
                {current.data?.session.id === s.id && <Badge>This browser</Badge>}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
